import React, { useState } from 'react';
import styled from 'styled-components';
import { FaChevronLeft, FaChevronRight, FaQuoteLeft } from 'react-icons/fa';

const TestimonialsSection = styled.section`
  padding: 120px ${({ theme }) => theme.spacing.lg};
  background: linear-gradient(180deg, ${({ theme }) => theme.colors.white} 0%, ${({ theme }) => theme.colors.background} 50%, ${({ theme }) => theme.colors.white} 100%);
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(0, 168, 168, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 80px ${({ theme }) => theme.spacing.md};
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.dark};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const SectionDescription = styled.p`
  color: ${({ theme }) => theme.colors.grey};
  font-size: ${({ theme }) => theme.fontSizes.lg};
`;

const CarouselContainer = styled.div`
  position: relative;
  max-width: 900px;
  margin: 0 auto;
`;

const TestimonialWrapper = styled.div`
  position: relative;
  min-height: 350px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 400px;
  }
`;

const TestimonialCard = styled.div`
  position: absolute;
  width: 100%;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transform: translateX(${({ $active }) => ($active ? '0' : '20px')});
  transition: all 0.5s ease-in-out;
  pointer-events: ${({ $active }) => ($active ? 'auto' : 'none')};
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing['2xl']};
  box-shadow: 0 15px 50px rgba(0, 102, 204, 0.12);
  border: 1px solid rgba(0, 102, 204, 0.08);
`;

const QuoteIcon = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Quote = styled.blockquote`
  color: ${({ theme }) => theme.colors.dark};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: 1.8;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  font-style: italic;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes.base};
  }
`;

const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Avatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}40, ${({ theme }) => theme.colors.secondary}40);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  flex-shrink: 0;
  border: 3px solid ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const AuthorName = styled.div`
  color: ${({ theme }) => theme.colors.dark};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const AuthorRole = styled.div`
  color: ${({ theme }) => theme.colors.grey};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing['3xl']};
`;

const NavButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.white};
  border: 2px solid ${({ theme }) => theme.colors.lightGrey};
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  transition: all ${({ theme }) => theme.transitions.base};
  
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
    border-color: ${({ theme }) => theme.colors.primary};
    transform: scale(1.1);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const Indicators = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const Indicator = styled.button`
  width: 12px;
  height: 12px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme, $active }) => 
    $active ? theme.colors.primary : theme.colors.lightGrey};
  border: none;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background: ${({ theme, $active }) => 
      $active ? theme.colors.primaryDark : theme.colors.grey};
  }
`;

const testimonials = [
  
  {
    quote: "I trust LOGG completely to take care of business so I can focus on quality of care.",
    author: "BH Provider",
    role: "Oregon",
    avatar: "👨‍💼"
  },
  {
    quote: "LOGG has allowed us to grow in the past several months.",
    author: "PCP",
    role: "New York",
    avatar: "👩‍⚕️"
  },
  {
    quote: "You guys are a lifesavers.",
    author: "Urologist",
    role: "Texas",
    avatar: "👨‍⚕️"
  }
];

export const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex((prev) => 
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prev) => 
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };

  const goToIndex = (index) => {
    setCurrentIndex(index);
  };

  return (
    <TestimonialsSection id="testimonials">
      <Container>
        <SectionHeader>
          <SectionTitle>What Healthcare Providers Say</SectionTitle>
          <SectionDescription>
            Trusted by practices across the United States
          </SectionDescription>
        </SectionHeader>
        
        <CarouselContainer>
          <TestimonialWrapper>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard 
                key={index} 
                $active={index === currentIndex}
                role="tabpanel"
                aria-hidden={index !== currentIndex}
              >
                <QuoteIcon aria-hidden="true">
                  <FaQuoteLeft />
                </QuoteIcon>
                <Quote>"{testimonial.quote}"</Quote>
                <AuthorSection>
                  <Avatar aria-hidden="true">
                    {testimonial.avatar}
                  </Avatar>
                  <AuthorInfo>
                    <AuthorName>{testimonial.author}</AuthorName>
                    <AuthorRole>{testimonial.role}</AuthorRole>
                  </AuthorInfo>
                </AuthorSection>
              </TestimonialCard>
            ))}
          </TestimonialWrapper>
          
          <Controls>
            <NavButton 
              onClick={goToPrevious}
              aria-label="Previous testimonial"
            >
              <FaChevronLeft />
            </NavButton>
            
            <Indicators role="tablist" aria-label="Testimonial navigation">
              {testimonials.map((_, index) => (
                <Indicator
                  key={index}
                  $active={index === currentIndex}
                  onClick={() => goToIndex(index)}
                  aria-label={`Go to testimonial ${index + 1}`}
                  aria-selected={index === currentIndex}
                  role="tab"
                />
              ))}
            </Indicators>
            
            <NavButton 
              onClick={goToNext}
              aria-label="Next testimonial"
            >
              <FaChevronRight />
            </NavButton>
          </Controls>
        </CarouselContainer>
      </Container>
    </TestimonialsSection>
  );
};
